import { useState, useRef, type ReactNode } from 'react';
import { ChevronsDown, X } from 'lucide-react';
import RemovePersonalisationDataModal from '../modals/remove-personalisation-data-modal/remove-personalisation-data-modal';
import UsersServices from '@/services/users-services';
import { ShowResponseMessage, ShowErrorResponseMessage } from '@/utility/api-utility';
import { PHONE_DISPLAY, EMAIL, PHONE_NUMBER, WHATSAPP_URL } from '@/constants/variables';

// import { getUrlParameters } from '@/utility/utility';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

import WhatsAppIcon from '../../assets/icons/whatsapp.png';
import { useStorage } from '@/utility/storage';
import { useStrings } from '@/contexts/strings-context';

interface AppFooterProps {
  showMoreDataIcon?: boolean;
  children?: ReactNode;
  className?: string;
}

const footerLinkClassName =
  'rounded-[2px] underline decoration-white/60 underline-offset-[3px] transition-colors hover:text-screwfix-yellow focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-screwfix-yellow/70';

function PrivacyNotice() {
  const [isRemoveDataModalOpen, setIsRemoveDataModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const strings = useStrings();

  const handleRemoveDataConfirm = async () => {
    // TODO

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const { data: response } = await UsersServices.forget();
      ShowResponseMessage({ response });
      setIsRemoveDataModalOpen(false);
    } catch (error) {
      ShowErrorResponseMessage({ error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrivacyContentClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    const removeDataLink = target.closest('[data-action="remove-data"]');

    if (!removeDataLink) {
      return;
    }

    e.preventDefault();
    setIsRemoveDataModalOpen(true);
  };

  const handleRemoveDataGoBack = () => setIsRemoveDataModalOpen(false);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className={footerLinkClassName} type="button">
          Privacy &amp; essential cookie notice
        </button>
      </DialogTrigger>

      <DialogContent
        className="max-h-[calc(100dvh-32px)] max-w-[calc(100%-32px)] gap-0 overflow-y-auto rounded-none border-border bg-background p-0 sm:max-w-[720px] text-[#636363]"
        showCloseButton={false}
      >
        <DialogHeader className="sticky top-0 z-10 flex-row items-start gap-[14px] px-[20px] py-[19px] pr-[62px] text-left text-secondary-foreground bg-[#3175b7] sm:px-[28px] sm:py-[23px] sm:pr-[72px]">
          <div>
            <DialogTitle className="mt-[4px] [font-family:archivo_narrowbold,sans-serif] text-[24px] font-bold leading-[28px] sm:text-[28px] sm:leading-[31px]">
              {strings.PRIVACY_TITLE}
            </DialogTitle>
          </div>

          <DialogClose asChild>
            <button
              aria-label="Close privacy notice"
              className="absolute cursor-pointer right-[16px] top-[17px] flex h-[40px] w-[40px] items-center justify-center rounded-full text-secondary-foreground transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-screwfix-yellow/70 sm:right-[22px] sm:top-[21px]"
              type="button"
            >
              <X aria-hidden="true" className="h-[22px] w-[22px]" />
            </button>
          </DialogClose>
        </DialogHeader>

        <div className="space-y-[17px] px-[20px] py-[22px] text-[clamp(16px,calc(14px_+_0.3vw),17px)] font-normal leading-[21px] sm:px-[28px] sm:py-[26px]">
          <DialogDescription>
            <span
              onClick={handlePrivacyContentClick}
              className="-mt-[18px] max-w-[450px] text-[14px] font-medium leading-[20px] text-slate-600 sm:text-[16px] font-[Arial]"
              dangerouslySetInnerHTML={{ __html: strings.PRIVACY_CONTENT }}
            />
          </DialogDescription>
        </div>
        <RemovePersonalisationDataModal
          open={isRemoveDataModalOpen}
          onGoBack={handleRemoveDataGoBack}
          onConfirm={handleRemoveDataConfirm}
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
}

const AppFooter = ({ showMoreDataIcon = false, children, className = '' }: AppFooterProps) => {
  const footerRef = useRef<HTMLElement>(null);
  const strings = useStrings();

  if (children) {
    return (
      <footer
        className={`relative z-[1] grid min-h-[10vh] w-full grid-cols-2 gap-4 p-4 text-secondary-foreground bg-[#3175b7] ${className}`}
      >
        {showMoreDataIcon ? (
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-[-18px] flex h-[36px] w-[48px] -translate-x-1/2 items-center justify-center rounded-t-[8px] bg-secondary"
          >
            <ChevronsDown className="h-[22px] w-[22px]" />
          </span>
        ) : null}
        {children}
      </footer>
    );
  }

  return (
    <footer
      ref={footerRef}
      className={`relative z-10 w-full shrink-0 text-secondary-foreground bg-[#3175b7] ${className}`}
    >
      <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-[9px] px-[16px] py-[14px] sm:px-[30px] lg:flex-row lg:items-center lg:justify-between lg:gap-[24px]">
        <p className=" leading-[18px] text-white/90">{strings.FOOTER_TEXT}</p>

        <nav
          aria-label="Privacy and support"
          className="flex flex-wrap items-center gap-x-[9px] gap-y-[7px] text-[16px] leading-[18px] pb-10 sm:py-10 sm-pb-0"
        >
          <span aria-hidden="true" className="hidden text-white/35 sm:inline"></span>
          <a className={footerLinkClassName} href={`tel:${PHONE_NUMBER}`}>
            {PHONE_DISPLAY}
          </a>
          <span aria-hidden="true" className="hidden text-white/35 sm:inline">
            |
          </span>
          <a className={footerLinkClassName} href={`mailto:${EMAIL}`}>
            {EMAIL}
          </a>
          <span aria-hidden="true" className="hidden text-white/35 sm:inline">
            |
          </span>

          <PrivacyNotice />
        </nav>
      </div>
      <WhatsAppSupport />
    </footer>
  );
};

export function WhatsAppSupport({ inVerification = false }: { inVerification?: boolean }) {
  const strings = useStrings();
  const reference = useStorage('reference');
  const message = strings.WHATSAPP_MESSAGE || '';
  const userMessage = reference ? `?text=${encodeURIComponent(message.replace('%s', reference))}` : '';
  const WHATSAPP_URL_WITH_ORDER = `${WHATSAPP_URL}${userMessage}`;

  return (
    <a
      data-whatsapp-support={inVerification ? 'verification' : 'footer'}
      aria-label="Open WhatsApp support in a new tab"
      className="pointer-events-auto group fixed bottom-[16px] right-[16px] z-[10000] flex h-[48px] w-[48px] items-center justify-center rounded-full bg-white text-success-foreground shadow-[0_6px_20px_rgba(6,34,70,0.3)] transition-[bottom,transform] duration-150 hover:-translate-y-[2px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-2 sm:bottom-[24px] sm:right-[30px]"
      href={WHATSAPP_URL_WITH_ORDER}
      rel="noreferrer"
      target="_blank"
      style={{ backgroundColor: 'white' }}
    >
      <span className="pointer-events-none absolute right-[58px] hidden whitespace-nowrap bg-secondary px-[11px] py-[7px] [font-family:archivo_narrowbold,sans-serif] text-[16px] font-bold text-secondary-foreground opacity-0 shadow-md transition-[opacity,transform] duration-150 group-hover:-translate-x-[2px] group-hover:opacity-100 group-focus-visible:-translate-x-[2px] group-focus-visible:opacity-100 sm:block">
        Chat on WhatsApp
      </span>

      <img alt="" aria-hidden="true" className="p-2" src={WhatsAppIcon} />
    </a>
  );
}

export default AppFooter;

{
  /* <span
            aria-hidden="true"
            className="mt-[1px] flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-screwfix-yellow text-screwfix-blue-shade-3"
          >
            <ShieldCheck className="h-[21px] w-[21px]" strokeWidth={2.4} />
          </span> */
}

// function PrivacyNotice() {
//   return (
//     <Dialog>
//       <DialogTrigger asChild>
//         <button className={footerLinkClassName} type="button">
//           Privacy &amp; essential cookie notice
//         </button>
//       </DialogTrigger>

//       <DialogContent
//         className="max-h-[calc(100dvh-32px)] max-w-[calc(100%-32px)] gap-0 overflow-y-auto rounded-none border-border bg-background p-0 text-foreground sm:max-w-[720px]"
//         showCloseButton={false}
//       >
//         <DialogHeader className="sticky top-0 z-10 flex-row items-start gap-[14px] bg-secondary px-[20px] py-[19px] pr-[62px] text-left text-secondary-foreground sm:px-[28px] sm:py-[23px] sm:pr-[72px]">
//           <span
//             aria-hidden="true"
//             className="mt-[1px] flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-screwfix-yellow text-screwfix-blue-shade-3"
//           >
//             <ShieldCheck className="h-[21px] w-[21px]" strokeWidth={2.4} />
//           </span>

//           <div>
//             <p className="[font-family:archivo_narrowbold,sans-serif] text-[12px] font-bold uppercase leading-[15px] tracking-[0.09em] text-screwfix-yellow">
//               Your information
//             </p>
//             <DialogTitle className="mt-[4px] [font-family:archivo_narrowbold,sans-serif] text-[23px] font-bold leading-[28px] sm:text-[26px] sm:leading-[31px]">
//               Privacy &amp; essential cookie notice
//             </DialogTitle>
//           </div>

//           <DialogClose asChild>
//             <button
//               aria-label="Close privacy notice"
//               className="absolute right-[16px] top-[17px] flex h-[40px] w-[40px] items-center justify-center rounded-full text-secondary-foreground transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-screwfix-yellow/70 sm:right-[22px] sm:top-[21px]"
//               type="button"
//             >
//               <X aria-hidden="true" className="h-[22px] w-[22px]" />
//             </button>
//           </DialogClose>
//         </DialogHeader>

//         <div className="space-y-[17px] px-[20px] py-[22px] text-[15px] font-normal leading-[21px] sm:px-[28px] sm:py-[26px]">
//           <DialogDescription className="text-[15px] leading-[21px] text-foreground">
//             Screwfix Direct Limited is responsible for the personal information used for your order. This
//             personalisation service is operated by MyClothing Limited on Screwfix&apos;s behalf.
//           </DialogDescription>

//           <p>
//             We use the order information provided by Screwfix, the email address you enter and your personalisation
//             choices to confirm your access and prepare your personalised garments. MyClothing uses this information only
//             to provide the service for Screwfix and retains it in accordance with Screwfix&apos;s instructions.
//           </p>

//           <section
//             aria-labelledby="essential-cookies-heading"
//             className="border border-border bg-accent p-[16px] sm:p-[18px]"
//           >
//             <h3
//               className="[font-family:archivo_narrowbold,sans-serif] text-[18px] font-bold leading-[22px] text-screwfix-blue-shade-3"
//               id="essential-cookies-heading"
//             >
//               Essential cookies
//             </h3>
//             <p className="mt-[7px]">
//               This service uses only the essential cookies listed below to authenticate you, keep your session secure,
//               remember your progress and check that you are using the latest version of the app. They are not used for
//               analytics, advertising or tracking.
//             </p>

//             <dl className="mt-[14px] grid gap-[10px]">
//               <div className="grid gap-[2px] border-t border-screwfix-blue-tint-2 pt-[10px] sm:grid-cols-[120px_1fr] sm:gap-[12px]">
//                 <dt className="[font-family:archivo_narrowbold,sans-serif] font-bold text-screwfix-blue-shade-3">
//                   PHPSESSID
//                 </dt>
//                 <dd>Authentication session; expires when your browser session ends.</dd>
//               </div>
//               <div className="grid gap-[2px] border-t border-screwfix-blue-tint-2 pt-[10px] sm:grid-cols-[120px_1fr] sm:gap-[12px]">
//                 <dt className="[font-family:archivo_narrowbold,sans-serif] font-bold text-screwfix-blue-shade-3">
//                   appVersion
//                 </dt>
//                 <dd>App update check; expires 365 days after it is set.</dd>
//               </div>
//             </dl>
//           </section>

//           <p>
//             For details about how Screwfix uses your personal information and your data-protection rights, read the{' '}
//             <a className={noticeLinkClassName} href={SCREWFIX_PRIVACY_URL} rel="noreferrer" target="_blank">
//               Screwfix Privacy Notice
//               <ExternalLink aria-hidden="true" className="h-[14px] w-[14px]" />
//             </a>
//             .
//           </p>

//           <section aria-labelledby="personalisation-help-heading" className="border-t border-border pt-[16px]">
//             <h3
//               className="[font-family:archivo_narrowbold,sans-serif] text-[18px] font-bold leading-[22px] text-screwfix-blue-shade-3"
//               id="personalisation-help-heading"
//             >
//               Personalisation help
//             </h3>
//             <p className="mt-[7px]">For help with personalisation, contact MyClothing:</p>
//             <div className="mt-[10px] flex flex-col items-start gap-[9px] sm:flex-row sm:gap-[20px]">
//               <a className={noticeLinkClassName} href={`tel:${PHONE_NUMBER}`}>
//                 <Phone aria-hidden="true" className="h-[16px] w-[16px]" />
//                 {PHONE_DISPLAY}
//               </a>
//               <a className={noticeLinkClassName} href={WHATSAPP_URL} rel="noreferrer" target="_blank">
//                 <MessageCircle aria-hidden="true" className="h-[16px] w-[16px]" />
//                 WhatsApp support
//                 <ExternalLink aria-hidden="true" className="h-[14px] w-[14px]" />
//               </a>
//             </div>
//           </section>

//           <p className="border-t border-border pt-[16px] text-[13px] leading-[18px] text-muted-foreground">
//             MyClothing Limited is registered in England and Wales under company number 11707364. Registered office:
//             Dixon House, Old Heath Road, Wolverhampton WV1 2BF.
//           </p>
//         </div>
//       </DialogContent>
//     </Dialog>
//   );
// }

// const whatsappLinkRef = useRef<HTMLAnchorElement>(null);

// useEffect(() => {
//   const footer = footerRef.current;
//   const whatsappLink = whatsappLinkRef.current;

//   if (!footer || !whatsappLink || hasActionContent) return;

//   const updateWhatsAppPosition = () => {
//     const viewportPadding = window.innerWidth >= 640 ? 24 : 16;
//     const footerGap = 12;
//     const footerTop = footer.getBoundingClientRect().top;
//     const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
//     const visibleFooterHeight = Math.max(0, viewportHeight - footerTop);

//     whatsappLink.style.bottom = `${Math.max(viewportPadding, visibleFooterHeight + footerGap)}px`;
//   };

//   updateWhatsAppPosition();
//   window.addEventListener('resize', updateWhatsAppPosition);
//   document.addEventListener('scroll', updateWhatsAppPosition, true);

//   const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateWhatsAppPosition);
//   resizeObserver?.observe(footer);

//   return () => {
//     window.removeEventListener('resize', updateWhatsAppPosition);
//     document.removeEventListener('scroll', updateWhatsAppPosition, true);
//     resizeObserver?.disconnect();
//   };
// }, [hasActionContent]);

{
  /* <p>
            We use the order information provided by Screwfix, the email address you enter and your personalisation
            choices to confirm your access and prepare your personalised garments. MyClothing uses this information only
            to provide the service for Screwfix and retains it in accordance with Screwfix&apos;s instructions.
          </p>

          <section
            aria-labelledby="essential-cookies-heading"
            className="border border-border bg-white p-[16px] sm:p-[18px]"
          >
            <h3
              className="text-[19px] font-bold leading-[22px] text-screwfix-blue-shade-2"
              id="essential-cookies-heading"
            >
              Essential cookies
            </h3>
            <p className="mt-[7px]">
              This service uses only the essential cookies listed below to authenticate you, keep your session secure,
              remember your progress and check that you are using the latest version of the app. They are not used for
              analytics, advertising or tracking.
            </p>

            <dl className="mt-[14px] grid gap-[10px]">
              <div className="grid gap-[2px] border-t border-screwfix-blue-tint-2 pt-[10px] sm:grid-cols-[120px_1fr] sm:gap-[12px]">
                <dt className="font-bold text-screwfix-blue-shade-2">PHPSESSID</dt>
                <dd>Authentication session; expires when your browser session ends.</dd>
              </div>
            </dl>

            <dl className="mt-[14px] grid gap-[10px]">
              <div className="grid gap-[2px] border-t border-screwfix-blue-tint-2 pt-[10px] sm:grid-cols-[120px_1fr] sm:gap-[12px]">
                <dt className="font-bold text-screwfix-blue-shade-2">Saved personalisation details</dt>
                <dd>
                  Logos and artwork you upload are stored so we can fulfil your order and make them available for future
                  orders.
                  <br /> <br />
                  They are linked to the email address and/or telephone number provided with your order so we can
                  identify your saved personalisation for future orders.
                  <br /> <br />
                  <a
                    className="inline-flex items-center gap-[5px] rounded-[2px] text-primary underline underline-offset-[3px] transition-colors hover:text-screwfix-blue-shade-1 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30"
                    // href="This should go to a page explaining we will delete the associations with the customers logos and cannot be undone then a link to Confirm which calls api/v1.0/users/forget"
                    rel="noreferrer"
                    target="_blank"
                    onClick={() => setIsRemoveDataModalOpen(true)}
                  >
                    Remove my saved personalisation data
                  </a>
                  <br />
                  <br />
                  If you request removal, we will remove the link between your saved personalisation and your email
                  address and/or telephone number.
                  <br />
                  <br />
                  Artwork that has not been used on an order will also be removed. Artwork already used to fulfil an
                  order will be retained as part of the order record, but will not be available for future use.
                </dd>
              </div>
            </dl>
          </section>

          <p>
            For details about how Screwfix uses your personal information and your data-protection rights, read the{' '}
            <a className={noticeLinkClassName} href={SCREWFIX_PRIVACY_URL} rel="noreferrer" target="_blank">
              Screwfix Privacy Notice
              <ExternalLink aria-hidden="true" className="h-[14px] w-[14px]" />
            </a>
            .
          </p>

          <section aria-labelledby="personalisation-help-heading" className="border-t border-border pt-[16px]">
            <h3
              className="[font-family:archivo_narrowbold,sans-serif] text-[19px] font-bold leading-[22px] text-screwfix-blue-shade-3"
              id="personalisation-help-heading"
            >
              Personalisation help
            </h3>
            <p className="mt-[7px]">For help with personalisation, contact MyClothing:</p>
            <div className="mt-[10px] flex flex-col items-start gap-[9px] sm:flex-row sm:gap-[20px]">
              <a className={noticeLinkClassName} href={`tel:${PHONE_NUMBER}`}>
                <Phone aria-hidden="true" className="h-[16px] w-[16px]" />
                {PHONE_DISPLAY}
              </a>
              <a className={noticeLinkClassName} href={WHATSAPP_URL} rel="noreferrer" target="_blank">
                <MessageCircle aria-hidden="true" className="h-[16px] w-[16px]" />
                WhatsApp support
                <ExternalLink aria-hidden="true" className="h-[14px] w-[14px]" />
              </a>
            </div>
          </section>

          <p className="border-t border-border pt-[16px] text-[16px] leading-[18px] text-muted-foreground">
            MyClothing Limited is registered in England and Wales under company number 11707364. Registered office:
            Dixon House, Old Heath Road, Wolverhampton WV1 2BF.
          </p> */
}
